import { languageCodeToName, toIetfLanguageTag } from './language';

function matchAnyCode(code: string, words: { [code: string]: string }) {
  const languageCode = toIetfLanguageTag(code);
  const components = languageCode.split('-');

  for (let end = components.length; end > 0; end -= 1) {
    const codeToTest = components.slice(0, end).join('-');
    if (words[codeToTest]) return words[codeToTest];
  }
  return null;
}

/**
 * Based on the ISO 639-1
 * Intentionally not using `_t`, since we want to keep the language names in their own language
 * Returns the English (translated to the user's language) if not found.
 *
 * The data source for language local names is https://wikistats.wmcloud.org/display.php?t=wp,
 * which is used by Wikipedia, unless with other source in comment.
 */
function languageTagToLocalName(code: string): string {
  const names = {
    aa: 'Afar',
    ab: 'Аҧсуа',
    // https://en.wikipedia.org/wiki/Avestan
    ae: '𐬎𐬞𐬀𐬯𐬙𐬀𐬎𐬎𐬀𐬐𐬀𐬉𐬥𐬀',
    af: 'Afrikaans',
    ak: 'Akana',
    am: 'አማርኛ',
    an: 'Aragonés',
    ar: 'العربية',
    'ar-EG': 'مصرى (Maṣrī)',
    as: 'অসমীয়া',
    av: 'Авар',
    ay: 'Aymar',
    az: 'Azərbaycan dili',
    ba: 'Башҡорт',
    be: 'Беларуская',
    bg: 'Български',
    bh: 'भोजपुरी',
    bi: 'Bislama',
    bm: 'Bamanankan',
    bn: 'বাংলা',
    bo: 'བོད་སྐད',
    br: 'Brezhoneg',
    bs: 'Bosanski',
    ca: 'Català',
    ce: 'Нохчийн',
    ch: 'Chamoru',
    co: 'Corsu',
    cr: 'Nehiyaw',
    cs: 'Čeština',
    cu: 'Словѣньскъ',
    cv: 'Чăваш',
    cy: 'Cymraeg',
    da: 'Dansk',
    de: 'Deutsch',
    dv: 'ދިވެހިބަސް',
    dz: 'ཇོང་ཁ',
    ee: 'Eʋegbe',
    el: 'Ελληνικά',
    en: 'English',
    eo: 'Esperanto',
    es: 'Español',
    et: 'Eesti',
    eu: 'Euskara',
    fa: 'فارسی',
    // https://en.wikipedia.org/wiki/Dari
    'fa-AF': 'دری',
    ff: 'Fulfulde',
    fi: 'Suomi',
    fj: 'Na Vosa Vakaviti',
    fo: 'Føroyskt',
    fr: 'Français',
    fy: 'Frysk',
    ga: 'Gaeilge',
    gd: 'Gàidhlig',
    gl: 'Galego',
    gn: "Avañe'ẽ",
    gu: 'ગુજરાતી',
    gv: 'Gaelg',
    ha: 'هَوُسَ',
    he: 'עברית',
    hi: 'हिन्दी',
    ho: 'Hiri Motu',
    hr: 'Hrvatski',
    ht: 'Krèyol ayisyen',
    hu: 'Magyar',
    hy: 'Հայերեն',
    hz: 'Otsiherero',
    ia: 'Interlingua',
    id: 'Bahasa Indonesia',
    ig: 'Igbo',
    ii: 'ꆇꉙ',
    ik: 'Iñupiak',
    io: 'Ido',
    is: 'Íslenska',
    it: 'Italiano',
    iu: 'ᐃᓄᒃᑎᑐᑦ',
    ja: '日本語',
    // https://en.wikipedia.org/wiki/Javanese_language
    jv: 'Basa Jawa / ꦧꦱꦗꦮ',
    ka: 'ქართული',
    kg: 'KiKongo',
    ki: 'Gĩkũyũ',
    kj: 'Kuanyama',
    kk: 'Қазақша',
    kl: 'Kalaallisut',
    km: 'ភាសាខ្មែរ',
    kn: 'ಕನ್ನಡ',
    ko: '한국어',
    kr: 'Kanuri',
    ks: 'कश्मीरी / كشميري',
    ku: 'Kurdî / كوردی',
    kv: 'Коми',
    kw: 'Kernewek / Karnuack',
    ky: 'Кыргызча',
    la: 'Latina',
    lb: 'Lëtzebuergesch',
    lg: 'Luganda',
    li: 'Limburgs',
    ln: 'Lingala',
    lo: 'ລາວ',
    lt: 'Lietuvių',
    // https://en.wikipedia.org/wiki/Luba-Katanga_language
    lu: 'Tshilubà',
    lv: 'Latviešu',
    mg: 'Malagasy',
    mh: 'Ebon',
    mi: 'Māori',
    mk: 'Македонски',
    ml: 'മലയാളം',
    mn: 'Монгол',
    mr: 'मराठी',
    ms: 'Bahasa Melayu',
    mt: 'Malti',
    my: 'မြန်မာဘာသာ',
    na: 'Dorerin Naoero',
    // https://en.wikipedia.org/wiki/Bokm%C3%A5l
    nb: 'Norsk (Bokmål)',
    // https://en.wikipedia.org/wiki/Northern_Ndebele_language
    nd: 'North Ndebele',
    ne: 'नेपाली',
    ng: 'Oshiwambo',
    nl: 'Nederlands',
    // https://en.wikipedia.org/wiki/Nynorsk
    nn: 'Norsk (Nynorsk)',
    // https://en.wikipedia.org/wiki/Norwegian_language
    no: 'Norsk',
    // https://en.wikipedia.org/wiki/Southern_Ndebele_language
    nr: 'South Ndebele',
    nv: 'Diné bizaad',
    ny: 'Chi-Chewa',
    oc: 'Occitan',
    // https://en.wikipedia.org/wiki/Ojibwe_language
    oj: 'Anishinaabemowin / ᐊᓂᐦᔑᓈᐯᒧᐎᓐ',
    om: 'Oromoo',
    or: 'ଓଡ଼ିଆ',
    os: 'Иронау',
    pa: 'ਪੰਜਾਬੀ',
    pi: 'पाऴि',
    pl: 'Polski',
    ps: 'پښتو',
    pt: 'Português',
    // chrome://settings/languages
    'pt-BR': 'Português (Brasil)',
    // chrome://settings/languages
    'pt-PT': 'Português (Portugal)',
    qu: 'Runa Simi',
    rm: 'Rumantsch',
    rn: 'Kirundi',
    ro: 'Română',
    ru: 'Русский',
    rw: 'Ikinyarwanda',
    sa: 'संस्कृतम्',
    sc: 'Sardu',
    sd: 'سنڌي، سندھی ، सिन्ध',
    se: 'Sámegiella',
    sg: 'Sängö',
    si: 'සිංහල',
    sk: 'Slovenčina',
    sl: 'Slovenščina',
    sm: 'Gagana Samoa',
    sn: 'chiShona',
    so: 'Soomaaliga',
    sq: 'Shqip',
    sr: 'Српски / Srpski',
    ss: 'SiSwati',
    st: 'Sesotho',
    su: 'Basa Sunda',
    sv: 'Svenska',
    sw: 'Kiswahili',
    ta: 'தமிழ்',
    te: 'తెలుగు',
    tg: 'Тоҷикӣ',
    th: 'ไทย',
    ti: 'ትግርኛ',
    tk: 'تركمن / Туркмен',
    tl: 'Tagalog',
    tn: 'Setswana',
    to: 'faka Tonga',
    tr: 'Türkçe',
    ts: 'Xitsonga',
    tt: 'Tatarça / Татарча',
    tw: 'Twi',
    ty: 'Reo Mā`ohi',
    ug: 'ئۇيغۇر تىلى',
    uk: 'Українська',
    ur: 'اردو',
    uz: 'O‘zbek',
    ve: 'Tshivenḓa',
    vi: 'Tiếng Việt',
    vo: 'Volapük',
    wa: 'Walon',
    wo: 'Wolof',
    xh: 'isiXhosa',
    yi: 'ייִדיש',
    yo: 'Yorùbá',
    za: 'Cuengh',
    zh: '中文',
    // chrome://settings/languages
    'zh-CN': '中文（简体）',
    // chrome://settings/languages
    'zh-TW': '中文（繁體）',
    zu: 'isiZulu',
  };

  const nativeLanguage = matchAnyCode(code, names);
  if (nativeLanguage) return nativeLanguage;
  return languageCodeToName(code);
}

const getLabelForAITranslation = (code: string): string => {
  // generated by chatgpt as i dont speak all of these languages
  const languages = {
    aa: 'Afar (AI-generated)',
    ab: 'Аҧсуа (ИИ-ҵакыз)',
    ae: '𐬎𐬞𐬀𐬯𐬙𐬀𐬎𐬎𐬀𐬐𐬀𐬉𐬥𐬀 (𐬀𐬉𐬙𐬵𐬀𐬭𐬀𐬘𐬵𐬀𐬉)',
    af: 'Afrikaans (Deur KI gegenereer)',
    ak: 'Akana (Ahedzemu nipa AI)',
    am: 'አማርኛ (እንደ ቀረበ በAI)',
    an: 'Aragonés (Generau per IA)',
    ar: 'العربية (مولدة بواسطة الذكاء الاصطناعي)',
    'ar-EG': 'مصرى (مولدة بواسطة الذكاء الاصطناعي) (Maṣrī)',
    as: 'অসমীয়া (এ আই জন্মুৱাৰ)',
    av: 'Авар (Сгенерирано AI)',
    ay: 'Aymar (Generado por IA)',
    az: 'Azərbaycanca (Süni intellekt tərəfindən yaradılmış)',
    ba: 'Башҡорт (AI-менән булдырылған)',
    be: 'Беларуская (Створаны AI)',
    bg: 'Български (Генерирано от ИИ)',
    bh: 'भोजपुरी (एआई-जनित)',
    bi: 'Bislama (I create wetem AI)',
    bm: 'Bamanankan (İ tɛmɛnen dɔ tɛɛ yɛ AI)',
    bn: 'বাংলা (এআই-উত্পন্ন)',
    bo: 'བོད་སྐད (རང་བཞིན་གྱི་ AI ལས་བསྐྲུན་ཡོད་པ)',
    br: 'Brezhoneg (Dermat gant IA)',
    bs: 'Bosanski (Generisano AI)',
    ca: 'Català (Generat per IA)',
    ce: 'Нохчийн (Роди АИ)',
    ch: 'Chamoru (Produktu na AI)',
    co: 'Corsu (Generatu da AI)',
    cr: 'Nehiyaw (Otehiki AI)',
    cs: 'Čeština (Generováno AI)',
    cu: 'Словѣньскъ (AI-сътвореный)',
    cv: 'Чăваш (AI-ра турă)',
    cy: 'Cymraeg (Cynhyrchwyd gan AI)',
    da: 'Dansk (Genereret af AI)',
    de: 'Deutsch (KI-generiert)',
    dv: 'ދިވެހިބަސް (އަނާގެ AI ތާރީޚް)',
    dz: 'ཇོང་ཁ (Al-སྒྲིག��ཡོད་པའི་)',
    ee: 'Eʋegbe (AI-dom.)',
    el: 'Ελληνικά (Δημιουργημένα από AI)',
    en: 'English (AI-generated)',
    eo: 'Esperanto (Kreita per AI)',
    es: 'Español (Generado por IA)',
    et: 'Eesti (Loodud AI poolt)',
    eu: 'Euskara (Sortua AI-orekin)',
    fa: 'فارسی (تولید شده توسط هوش مصنوعی)',
    'fa-AF': 'دری (تولید شده توسط هوش مصنوعی)',
    ff: 'Fulfulde (Haayni AI)',
    fi: 'Suomi (Luotu AI:n avulla)',
    fj: 'Na Vosa Vakaviti (A cava vei AI)',
    fo: 'Føroyskt (Búið til við AI)',
    fr: 'Français (Généré par IA)',
    fy: 'Frysk (AI makke)',
    ga: 'Gaeilge (Cruthaithe ag AI)',
    gd: 'Gàidhlig (Air a chruthachadh le AI)',
    gl: 'Galego (Xenerado por IA)',
    gn: "Avañe'ẽ (AI-guerekóva)",
    gu: 'ગુજરાતી (AI નિર્માણ)',
    gv: 'Gaelg (Croo liorish AI)',
    ha: 'هَوُسَ (AI Haifar da Hausa)',
    he: 'עברית (נוצר מדע מחשב)',
    hi: 'हिन्दी (AI-जनित)',
    ho: 'Hiri Motu (Emeno mo ai)',
    hr: 'Hrvatski (Generirano AI)',
    ht: 'Krèyol ayisyen (Jenere pa AI)',
    hu: 'Magyar (AI által generált)',
    hy: 'Հայերեն (Ստեղծված է AI-ի կողմից)',
    hz: 'Otsiherero (Yiranganwa AI)',
    ia: 'Interlingua (Generate per IA)',
    id: 'Bahasa Indonesia (Dihasilkan oleh AI)',
    ig: 'Igbo (E kere gbara AI)',
    ii: 'ꆇꉙ (AI ꆇꉙ པ AI པ)',
    ik: 'Iñupiak (Aḷa AI)',
    io: 'Ido (Kreita per AI)',
    is: 'Íslenska (Búið til með AI)',
    it: 'Italiano (Generato da IA)',
    iu: 'ᐃᓄᒃᑎᑐᑦ (AI ᐊᑐᒃᓯᒋᓯᒃ)',
    ja: '日本語 (AI生成)',
    jv: 'Basa Jawa / ꦧꦱꦗꦮ (Digawe dening AI / ꦤꦶꦪꦠ꧀ ꦱꦭꦆꦏ꧀ AI)',
    ka: 'ქართული (გენერირებული AI-ის მიერ)',
    kg: 'KiKongo (Ikenerate na AI)',
    ki: 'Gĩkũyũ (Rĩbĩkĩria na AI)',
    kj: 'Kuanyama (Uunavahikola ko AI)',
    kk: 'Қазақша (AI арқылы жасалған)',
    kl: 'Kalaallisut (Genereret af AI)',
    km: 'ភាសាខ្មែរ (បង្កើតដោយ AI)',
    kn: 'ಕನ್ನಡ (AI-ಉತ್ಪನ್ನ)',
    ko: '한국어 (AI 생성)',
    kr: 'Kanuri (Generet b AI)',
    ks: 'कश्मीरी / كشميري (एआई-जनरेटेड / AI-إنتاج)',
    ku: 'Kurdî / كوردی (دروستکراوی AI / Olîstîa AI)',
    kv: 'Коми (Сгенериранный искуственным интеллектом)',
    kw: 'Kernewek / Karnuack (Genevdhysi ai)',
    ky: 'Кыргызча (AI тарабынан түзүлгөн)',
    la: 'Latina (Generatum ab AI)',
    lb: 'Lëtzebuergesch (Kräert AI)',
    lg: 'Luganda (Genereedeko AI)',
    li: 'Limburgs (Genaereerd door AI)',
    ln: 'Lingala (Ebongisami na AI)',
    lo: 'ລາວ (ຕອດໂດ້ AI)',
    lt: 'Lietuvių (Sukūrė AI)',
    lu: 'Tshilubà (Mu kanji ku AI)',
    lv: 'Latviešu (Aizsūtīts ar AI)',
    mg: 'Malagasy (Noforonin AI)',
    mh: 'Ebon (Kōnono kōm AI)',
    mi: 'Māori (Hangaia e AI)',
    mk: 'Македонски (Генерирано со AI)',
    ml: 'മലയാളം (AI-ഉൽപ്പന്നം)',
    mn: 'Монгол (AI үүсгэсэн)',
    mr: 'मराठी (एआय-उत्पन्न)',
    ms: 'Bahasa Melayu (Hasil AI)',
    mt: 'Malti (Ġenerat mill-AI)',
    my: 'မြန်မာမှတ်တမ်း (AI-၏ ပြုလုပ်ထားသော)',
    na: 'Dorerin Naoero (Etetem AI)',
    nb: 'Norsk (Bokmål) (Generert av AI)',
    nd: 'North Ndebele (AI-yenziwe)',
    ne: 'नेपाली (AI उत्पन्न)',
    ng: 'Oshiwambo (Hepake yi AI)',
    nl: 'Nederlands (Gecreëerd door AI)',
    nn: 'Norsk (Nynorsk) (Generert av AI)',
    no: 'Norsk (Generert av AI)',
    nr: 'South Ndebele (AI-yenziwe)',
    nv: 'Diné bizaad (Haką́ ai)',
    ny: 'Chi-Chewa (Chopangidwa ndi AI)',
    oc: 'Occitan (Generat per AI)',
    oj: 'Anishinaabemowin / ᐊᓂᐦᔑᓈᐯᒧᐎᓐ (AI odinidwa)',
    om: 'Oromoo (kan AI keessatti uumame)',
    or: 'ଓଡ଼ିଆ (AI ସୃଷ୍ଟ)',
    os: 'Иронау (АИ код дзауджыт)',
    pa: 'ਪੰਜਾਬੀ (AI-ਉਤਪਾਦਤ)',
    pi: 'पाऴि (AI-जनित)',
    pl: 'Polski (Wygenerowane przez AI)',
    ps: 'پښتو (د AI لخوا توليد شوی)',
    pt: 'Português (Gerado por IA)',
    'pt-BR': 'Português (Brasil) (Gerado por IA)',
    'pt-PT': 'Português (Portugal) (Gerado por IA)',
    qu: 'Runa Simi (Kawsaq AI)',
    rm: 'Rumantsch (Generà da AI)',
    rn: 'Kirundi (Ikorewe na AI)',
    ro: 'Română (Generat de AI)',
    ru: 'Русский (Сгенерировано ИИ)',
    rw: 'Ikinyarwanda (Ikorewe na AI)',
    sa: 'संस्कृतम् (AI उत्पादित)',
    sc: 'Sardu (Generadu dae AI)',
    sd: 'سنڌي، سندھی ، सिन्ध (AI-ٽوڪيل)',
    se: 'Sámegiella (Generert av AI)',
    sg: 'Sängö (Genere da AI)',
    si: 'සිංහල (AI විසින් නිර්මාණය කරන ලදි)',
    sk: 'Slovenčina (Generované AI)',
    sl: 'Slovenščina (Ustvarjeno z AI)',
    sm: 'Gagana Samoa (Fausiaina e AI)',
    sn: 'chiShona (INAGUMBIANELANO PA NE AI)',
    so: 'Soomaaliga (U samayskay AI)',
    sq: 'Shqip (Gjeneruar nga AI)',
    sr: 'Српски / Srpski (Генерисано ИИ / Generisani od AI)',
    ss: 'SiSwati (AI yentwe)',
    st: 'Sesotho (E hlahisoa ke AI)',
    su: 'Basa Sunda (Diproduksi ku AI)',
    sv: 'Svenska (Genererat av AI)',
    sw: 'Kiswahili (Imeundwa na AI)',
    ta: 'தமிழ் (AI உருவாக்கப்பட்டது)',
    te: 'తెలుగు (AI ఉత్పత్తి)',
    tg: 'Тоҷикӣ (AI иборат аст)',
    th: 'ไทย (สร้างโดย AI)',
    ti: 'ትግርኛ (የታዘዘ በ AI)',
    tk: 'تركمن / Туркмен (AI tarapyndan öndürilen)',
    tl: 'Tagalog (Binuo ng AI)',
    tn: 'Setswana (Segilwe ke AI)',
    to: 'faka Tonga (Fatu ʻa e AI)',
    tr: 'Türkçe (Yapay Zeka ile üretildi)',
    ts: 'Xitsonga (Yi endliwile hi AI)',
    tt: 'Tatarça / Татарча (AI тудыручан)',
    tw: 'Twi (Yɛ da asɛadeɛ AI)',
    ty: 'Reo Mā`ohi (Matatau i TE AI)',
    ug: 'ئۇيغۇر تىلى (AI)',
    uk: 'Українська (Створене AI)',
    ur: 'اردو (اے آئی پیدا کیا گیا)',
    uz: 'O‘zbek (AI tomonidan ishlab chiqarilgan)',
    ve: 'Tshivenḓa (AI inwalane)',
    vi: 'Tiếng Việt (Được tạo bởi AI)',
    vo: 'Volapük (Geprojektön mä AI)',
    wa: 'Walon (Produit pa AI)',
    wo: 'Wolof (Généré par IA)',
    xh: 'isiXhosa (Kudalwa yi-AI)',
    yi: 'ייִדיש (פון AI געמאכט)',
    yo: 'Yorùbá (Ṣẹda nipasẹ AI)',
    za: 'Cuengh (Swentgen hai AI)',
    zh: '中文 (由AI生成)',
    'zh-CN': '中文（简体） (由AI生成)',
    'zh-TW': '中文（繁體） (由AI生成)',
    zu: 'isiZulu (Okukhiqizwe yi-AI)',
  };
  return matchAnyCode(code, languages) || '';
};

const getLabelForOriginalTranslation = (code: string): string => {
  // generated by chatgpt as i dont speak all of these languages
  const languages = {
    aa: 'Afar (Asal)',
    ab: 'Аҧсуа (Оригинал)',
    ae: '𐬎𐬞𐬀𐬯𐬙𐬀𐬎𐬎𐬀𐬐𐬀𐬉𐬥𐬀 (𐬀𐬉𐬙𐬵𐬀𐬭𐬀𐬘𐬵𐬀𐬉)',
    af: 'Afrikaans (Oorspronklik)',
    ak: 'Akana (ɔfã)',
    am: 'አማርኛ (የጀመሪው)',
    an: 'Aragonés (Original)',
    ar: 'العربية (أصلية)',
    'ar-EG': 'مصرى (أصلية) (Maṣrī)',
    as: 'অসমীয়া (মূল)',
    av: 'Авар (Оригинал)',
    ay: 'Aymar (Original)',
    az: 'Azərbaycanca (Əsas)',
    ba: 'Башҡорт (Төп)',
    be: 'Беларуская (Арыгінал)',
    bg: 'Български (Оригинал)',
    bh: 'भोजपुरी (मूल)',
    bi: 'Bislama (Orijinal)',
    bm: 'Bamanankan (Farafinna)',
    bn: 'বাংলা (মূল)',
    bo: 'བོད་སྐད (འབྲིང་པོ)',
    br: 'Brezhoneg (Orin)',
    bs: 'Bosanski (Izvornik)',
    ca: 'Català (Original)',
    ce: 'Нохчийн (Хьалхи)',
    ch: 'Chamoru (Original)',
    co: 'Corsu (Uriginale)',
    cr: 'Nehiyaw (Ayimanit)',
    cs: 'Čeština (Původní)',
    cu: 'Словѣньскъ (Начальнѣ)',
    cv: 'Чăваш (Асăну)',
    cy: 'Cymraeg (Gwreiddiol)',
    da: 'Dansk (Original)',
    de: 'Deutsch (Original)',
    dv: 'ދިވެހިބަސް (މަތީގެ)',
    dz: 'ཇོང་ཁ (མངོན་མངོལ)',
    ee: 'Eʋegbe (Wɔnudɔ)',
    el: 'Ελληνικά (Πρωτότυπο)',
    en: 'English (Original)',
    eo: 'Esperanto (Origina)',
    es: 'Español (Original)',
    et: 'Eesti (Originaal)',
    eu: 'Euskara (Jatorrizkoa)',
    fa: 'فارسی (اصلی)',
    'fa-AF': 'دری (اصلی)',
    ff: 'Fulfulde (Asali)',
    fi: 'Suomi (Alkuperäinen)',
    fj: 'Na Vosa Vakaviti (Original)',
    fo: 'Føroyskt (Upprunalega)',
    fr: 'Français (Original)',
    fy: 'Frysk (Oarspronklik)',
    ga: 'Gaeilge (Bunaidh)',
    gd: 'Gàidhlig (Tùsail)',
    gl: 'Galego (Orixinal)',
    gn: "Avañe'ẽ (Ñe'ẽ ypykuérare)",
    gu: 'ગુજરાતી (મૂળ)',
    gv: 'Gaelg (Bun-)',
    ha: 'هَوُسَ (Asalin)',
    he: 'עברית (מקורית)',
    hi: 'हिन्दी (मूल)',
    ho: 'Hiri Motu (Oginol)',
    hr: 'Hrvatski (Izvornik)',
    ht: 'Krèyol ayisyen (Original)',
    hu: 'Magyar (Eredeti)',
    hy: 'Հայերեն (Բնօրինակ)',
    hz: 'Otsiherero (Yatjangutanywapo)',
    ia: 'Interlingua (Original)',
    id: 'Bahasa Indonesia (Asli)',
    ig: 'Igbo (Ezinụlọ)',
    ii: 'ꆇꉙ (ꄜꐎ)',
    ik: 'Iñupiak (Inuqatigiich)',
    io: 'Ido (Originala)',
    is: 'Íslenska (Upprunalega)',
    it: 'Italiano (Originale)',
    iu: 'ᐃᓄᒃᑎᑐᑦ (ᐊᓴᐅᒥᓴᔪᖅ)',
    ja: '日本語 (オリジナル)',
    jv: 'Basa Jawa / ꦧꦱꦗꦮ (Asli)',
    ka: 'ქართული (თავდაპირველი)',
    kg: 'KiKongo (Ya ntowo)',
    ki: 'Gĩkũyũ (Ya Mũmbũni)',
    kj: 'Kuanyama (Yiile-Taungu)',
    kk: 'Қазақша (Түпнұсқа)',
    kl: 'Kalaallisut (Qasigiannguit)',
    km: 'ភាសាខ្មែរ (ដើម)',
    kn: 'ಕನ್ನಡ (ಮೂಲ)',
    ko: '한국어 (원본)',
    kr: 'Kanuri (Kai-kai)',
    ks: 'कश्मीरी / كشميري (اصل / मूल)',
    ku: 'Kurdî / كوردی (Orjînal / ئەسلی)',
    kv: 'Коми (Йылыны)',
    kw: 'Kernewek / Karnuack (Orgyn)',
    ky: 'Кыргызча (Баштапкы)',
    la: 'Latina (Originale)',
    lb: 'Lëtzebuergesch (Original)',
    lg: 'Luganda (Mwanzo)',
    li: 'Limburgs (Oorspronkelek)',
    ln: 'Lingala (Ya solo)',
    lo: 'ລາວ (ຕົ້ນສາຫລະ)',
    lt: 'Lietuvių (Originalas)',
    lu: 'Tshilubà (Ya nseke)',
    lv: 'Latvie��u (Oriģināls)',
    mg: 'Malagasy (Tany)',
    mh: 'Ebon (Ekoniw)',
    mi: 'Māori (Taketake)',
    mk: 'Македонски (Оригинален)',
    ml: 'മലയാളം (അസല്‍)',
    mn: 'Монгол (Эхний)',
    mr: 'मराठी (मूळ)',
    ms: 'Bahasa Melayu (Asal)',
    mt: 'Malti (Oriġinali)',
    my: 'မြန်မာဘာသာ (မူရင်း)',
    na: 'Dorerin Naoero (Dorer)',
    nb: 'Norsk (Bokmål) (Opprinnelig)',
    nd: 'North Ndebele (Okwethu)',
    ne: 'नेपाली (मूल)',
    ng: 'Oshiwambo (Osha)',
    nl: 'Nederlands (Origineel)',
    nn: 'Norsk (Nynorsk) (Opphavleg)',
    no: 'Norsk (Opprinnelig)',
    nr: 'South Ndebele (Okwethu)',
    nv: 'Diné bizaad (Tʼáá hwóʼajítɨ́įgo)',
    ny: 'Chi-Chewa (Choyambirira)',
    oc: 'Occitan (Original)',
    oj: 'Anishinaabemowin / ᐊᓂᐦᔑᓈᐯᒧᐎᓐ (ᐊᓐᓂᐦᑌᐘᑳᑎᓯᑳ)',
    om: 'Oromoo (Durii)',
    or: 'ଓଡ଼ିଆ (ମୂଳ)',
    os: 'Иронау (Афтæнгæ)',
    pa: 'ਪੰਜਾਬੀ (ਅਸਲੇ)',
    pi: 'पाऴि (मूल)',
    pl: 'Polski (Oryginalny)',
    ps: 'پښتو (اصلي)',
    pt: 'Português (Original)',
    'pt-BR': 'Português (Brasil) (Original)',
    'pt-PT': 'Português (Portugal) (Original)',
    qu: 'Runa Simi (Originari)',
    rm: 'Rumantsch (Original)',
    rn: 'Kirundi (Umwimerere)',
    ro: 'Română (Original)',
    ru: 'Русский (Оригинал)',
    rw: 'Ikinyarwanda (Original)',
    sa: 'संस्कृतम् (मूल)',
    sc: 'Sardu (Originali)',
    sd: 'سنڌي، سندھی ، सिन्ध (اصل / मूल)',
    se: 'Sámegiella (Origiála)',
    sg: 'Sängö (Ködörö-gbätï)',
    si: 'සිංහල (මුල්)',
    sk: 'Slovenčina (Originálny)',
    sl: 'Slovenščina (Izviren)',
    sm: 'Gagana Samoa (Uluaʻi)',
    sn: 'chiShona (Yechokwadi)',
    so: 'Soomaaliga (Asal)',
    sq: 'Shqip (Origjinal)',
    sr: 'Српски / Srpski (Оригиналан / Originalan)',
    ss: 'SiSwati (Lokuqala)',
    st: 'Sesotho (Lerato la Khethema)',
    su: 'Basa Sunda (Original)',
    sv: 'Svenska (Original)',
    sw: 'Kiswahili (Asili)',
    ta: 'தமிழ் (அசல்)',
    te: 'తెలుగు (మూలం)',
    tg: 'Тоҷикӣ (Асл)',
    th: 'ไทย (ต้นฉบับ)',
    ti: 'ትግርኛ (መነሻ)',
    tk: 'تركمن / Туркмен (Esasy)',
    tl: 'Tagalog (Orihinal)',
    tn: 'Setswana (Mmaane)',
    to: 'faka Tonga (Feʻunga)',
    tr: 'Türkçe (Orijinal)',
    ts: 'Xitsonga (Xivumbeko)',
    tt: 'Tatarça / Татарча (Төп / Асун)',
    tw: 'Twi (Edu)',
    ty: 'Reo Mā`ohi (Tumū)',
    ug: 'ئۇيغۇر تىلى (ئەسەسىي)',
    uk: 'Українська (Оригінальний)',
    ur: 'اردو (اصل)',
    uz: 'O‘zbek (Asl)',
    ve: 'Tshivenḓa (Mathomo)',
    vi: 'Tiếng Việt (Bản gốc)',
    vo: 'Volapük (Visit)',
    wa: 'Walon (Oriynal)',
    wo: 'Wolof (Original)',
    xh: 'isiXhosa (Ekuqaleni)',
    yi: 'ייִדיש (אָריגינעל)',
    yo: 'Yorùbá (Àṣẹlẹ)',
    za: 'Cuengh (Gunyath)',
    zh: '中文 (原版)',
    'zh-CN': '中文（简体） (原版)',
    'zh-TW': '中文（繁體） (原版)',
    zu: 'isiZulu (Eyoqobo)',
  };
  return matchAnyCode(code, languages) || '';
};

// Created with Chat-GPT, since i don't speak these languages
function getLabelForAutotranslation(code: string): string {
  const translations: { [key: string]: string } = {
    de: 'Auto',
    ru: 'авто',
    zh: '自动',
    ja: 'オート',
    ko: '자동',
    ar: 'آلي',
    fr: 'aut.',
    hi: 'ऑटो',
    he: 'אוטומטי',
    tr: 'otomatik',
    hu: 'automata',
    el: 'αυτόματο',
    id: 'otomatis',
    th: 'อัตโนมัติ',
    vi: 'tự động',
    tl: 'awto',
    af: 'outomaties',
    sq: 'automatik',
    uk: 'авто',
    bg: 'авто',
    et: 'automaatne',
    fa: 'خودکار',
    hr: 'automatski',
    ht: 'otomatik',
    hy: 'ավտոմատ',
    ka: 'ავტომატური',
    lt: 'automatinis',
    lv: 'automātisks',
    mk: 'автоматско',
    mr: 'ऑटो',
    pl: 'automatyczny',
    ro: 'automat',
    sl: 'avtomatsko',
    sr: 'аутоматски',
    uz: 'avtomatik',
    az: 'avto',
    bn: 'স্বয়ংক্রিয়',
    da: 'auto',
    'fa-AF': 'خودکار',
    ml: 'ഓട്ടോ',
    ps: 'خودکار',
    'pt-PT': 'automático',
    ta: 'ஆட்டோ',
    ur: 'خودکار',
  };
  return matchAnyCode(code, translations) || 'auto';
}

export { languageTagToLocalName, getLabelForAutotranslation, getLabelForAITranslation, getLabelForOriginalTranslation };
